import moment from "moment";

import { formatNumber } from "utils";

import {
  AgronomicalEvaluationAggregationsByYear,
  CarbonProgramOutcomesResponse,
  EVALUATION_METRICS,
  EVALUATION_METRICS_UNIT_OF_MEASURE,
  FieldEvaluationMetricCorrectionItem,
  FieldEvaluationMetricCorrectionsResponse,
  IMPACT_PANEL_CALCULATION_TYPE,
  NullableRangeObject,
  SingleFieldOutcomes,
  VIEW_TYPE,
} from "models";

type EvaluationMetricDatasetItem = {
  label: string;
  data: NullableRangeObject;
};

type EvaluationMetricDatasets = {
  [EVALUATION_METRICS.HUMUS]: EvaluationMetricDatasetItem[];
  [EVALUATION_METRICS.SOC]: EvaluationMetricDatasetItem[];
  [EVALUATION_METRICS.CO2]: EvaluationMetricDatasetItem[];
} | null;

type EvaluationMetricYearRange = {
  firstYear: string;
  lastYear: string;
  validYears: string[];
};

type EvaluationMetricYearRanges = {
  [K in EVALUATION_METRICS]?: EvaluationMetricYearRange;
};

const METRICS_VALUES = Object.values(EVALUATION_METRICS);

// // we want to fill the year gaps with null values to keep a consistent graph scale
const fillYearGaps = (datasets: EvaluationMetricDatasets): EvaluationMetricDatasets => {
  if (!datasets || Object.keys(datasets).length === 0 || Object.values(datasets).every((dataset) => dataset.length === 0)) return null;

  const result = Object.entries(datasets).reduce(
    (output, [metric, dataset]) => {
      // we remove the null values, to get the correct first and last year
      // then we fill out the gaps with null values to keep a consistent x axis scale
      const validYears = dataset.filter((year) => year.data.avg !== null);

      const firstYear = parseInt(validYears[0]?.label);
      const lastYear = validYears.length < 2 ? firstYear : parseInt(validYears[validYears.length - 1]?.label);

      const totalYears = lastYear - firstYear + 1;
      const yearRange = Array.from({ length: totalYears }, (_, i) => firstYear + i);

      const filledDataset = yearRange.map((year) => {
        const yearStr = year.toString();
        const yearDataset = dataset.find((item) => item.label === yearStr);

        if (yearDataset) {
          return yearDataset;
        }

        return {
          label: yearStr,
          data: {
            min: null,
            avg: null,
            max: null,
          },
        };
      });

      return {
        ...output,
        [metric]: filledDataset,
      };
    },
    {} as Record<string, EvaluationMetricDatasetItem[]>
  );

  return result as EvaluationMetricDatasets;
};

export const getEvaluationMetricDatasets = (
  viewType: VIEW_TYPE,
  fieldId: string | undefined,
  fieldSizes: Record<string, number>,
  carbonProgramOutcomes: CarbonProgramOutcomesResponse,
  calculationType: IMPACT_PANEL_CALCULATION_TYPE,
  existingCorrectionsByFieldId: FieldEvaluationMetricCorrectionsResponse
): EvaluationMetricDatasets => {
  // simulations are out of scope for now
  if (viewType === VIEW_TYPE.SIMULATION) return null;

  if (viewType === VIEW_TYPE.SINGLE_FIELD && fieldId) {
    const fieldCarbonProgramOutcomes = carbonProgramOutcomes[fieldId];
    if (!fieldCarbonProgramOutcomes || "error" in fieldCarbonProgramOutcomes) return null;

    const { agronomicalEvaluationMetrics } = fieldCarbonProgramOutcomes;
    if (!agronomicalEvaluationMetrics) return null;

    const fieldSize = fieldSizes[fieldId] || 0;
    const metrics: EVALUATION_METRICS[] = [...METRICS_VALUES];
    const corrections = existingCorrectionsByFieldId[fieldId] ?? {};

    const [humusDataset, socDataset, co2Dataset] = metrics.map((metric) => {
      // we allow only one correction per metric per field
      const metricCorrectionDelta = getCorrectionDelta(metric, agronomicalEvaluationMetrics, corrections[metric]?.[0]);

      return Object.entries(agronomicalEvaluationMetrics).map(([year, yearData]) => {
        const metricData = yearData[metric];

        if ((metric === EVALUATION_METRICS.SOC || metric === EVALUATION_METRICS.CO2) && fieldSize > 0) {
          // soc and CO2 content are measured in kg, so we convert to tons
          const valueInTons = {
            min: metricData?.min ? metricData.min / 1000 + metricCorrectionDelta : null,
            avg: metricData?.avg ? metricData.avg / 1000 + metricCorrectionDelta : null,
            max: metricData?.max ? metricData.max / 1000 + metricCorrectionDelta : null,
          };

          if (calculationType === IMPACT_PANEL_CALCULATION_TYPE.TOTAL) {
            // for TOTAL mode, return the total tons without dividing by field size
            return {
              label: year,
              data: valueInTons,
            };
          }

          // For PER_HA mode, divide by field size to get t/ha
          return {
            label: year,
            data: {
              min: valueInTons.min ? valueInTons.min / fieldSize : null,
              avg: valueInTons.avg ? valueInTons.avg / fieldSize : null,
              max: valueInTons.max ? valueInTons.max / fieldSize : null,
            },
          };
        }

        return {
          label: year,
          data: {
            min: metricData?.min ? metricData.min + metricCorrectionDelta : null,
            avg: metricData?.avg ? metricData.avg + metricCorrectionDelta : null,
            max: metricData?.max ? metricData.max + metricCorrectionDelta : null,
          },
        };
      });
    });

    return fillYearGaps({
      [EVALUATION_METRICS.HUMUS]: humusDataset,
      [EVALUATION_METRICS.SOC]: socDataset,
      [EVALUATION_METRICS.CO2]: co2Dataset,
    });
  }

  if (viewType === VIEW_TYPE.ALL_FIELDS) {
    const validFieldOutcomes = Object.entries(carbonProgramOutcomes)
      .filter(([_, fieldOutcome]) => !("error" in fieldOutcome))
      .map(([fieldId, fieldOutcome]) => ({
        fieldId,
        fieldOutcome: fieldOutcome as SingleFieldOutcomes,
        fieldSize: fieldSizes[fieldId] || 0,
      }))
      .filter(({ fieldOutcome }) => fieldOutcome.fieldOutcomes.crops?.years);

    if (validFieldOutcomes.length === 0) return null;

    const allYears = validFieldOutcomes.reduce((years, { fieldOutcome }) => {
      if (fieldOutcome.fieldOutcomes.crops?.years) {
        Object.keys(fieldOutcome.fieldOutcomes.crops.years).forEach((year) => years.add(year));
      }
      return years;
    }, new Set<string>());

    const sortedYears = Array.from(allYears).sort();

    const metrics: EVALUATION_METRICS[] = [...METRICS_VALUES];

    const [humusDataset, socDataset, co2Dataset] = metrics.map((metric) =>
      sortedYears.map((year) => {
        const yearData = validFieldOutcomes.reduce(
          (data, { fieldOutcome, fieldSize }) => {
            const agronomicalEvaluationMetricsForField = fieldOutcome.agronomicalEvaluationMetrics?.[year];
            if (agronomicalEvaluationMetricsForField) {
              const metricData = agronomicalEvaluationMetricsForField[metric];

              // we allow only one correction per metric per field
              const fieldMetricCorrection = existingCorrectionsByFieldId[fieldOutcome.fieldId]?.[metric]?.[0];
              const metricCorrectionDelta = getCorrectionDelta(metric, fieldOutcome.agronomicalEvaluationMetrics, fieldMetricCorrection);

              if (metricData) {
                data.push({
                  min: metricData.min ? metricData.min : null,
                  avg: metricData.avg ? metricData.avg : null,
                  max: metricData.max ? metricData.max : null,
                  fieldSize,
                  metricCorrectionDelta,
                });
              }
            }
            return data;
          },
          [] as Array<{
            min: number | null;
            avg: number | null;
            max: number | null;
            fieldSize: number;
            metricCorrectionDelta: number;
          }>
        );

        if (yearData.length === 0) {
          return {
            label: year,
            data: {
              min: null,
              avg: null,
              max: null,
            },
          };
        }

        // soc and CO2 content are measured in kg, so we convert to tons and then calculate based on toggle option
        if (metric === EVALUATION_METRICS.SOC || metric === EVALUATION_METRICS.CO2) {
          // Filter out null values for carbon dioxide calculation
          const validYearData = yearData.filter((data) => data.min !== null && data.avg !== null && data.max !== null);

          if (validYearData.length === 0) {
            return {
              label: year,
              data: {
                min: null,
                avg: null,
                max: null,
              },
            };
          }

          // Convert from kg to tons
          const validYearDataInTons = validYearData.map((data) => ({
            min: data.min! / 1000 + data.metricCorrectionDelta,
            avg: data.avg! / 1000 + data.metricCorrectionDelta,
            max: data.max! / 1000 + data.metricCorrectionDelta,
            fieldSize: data.fieldSize,
          }));

          if (calculationType === IMPACT_PANEL_CALCULATION_TYPE.TOTAL) {
            // For TOTAL mode, sum all the tons across all fields
            const totalMin = validYearDataInTons.reduce((sum, data) => sum + data.min, 0);
            const totalAvg = validYearDataInTons.reduce((sum, data) => sum + data.avg, 0);
            const totalMax = validYearDataInTons.reduce((sum, data) => sum + data.max, 0);

            return {
              label: year,
              data: {
                min: totalMin,
                avg: totalAvg,
                max: totalMax,
              },
            };
          }

          // For PER_HA mode, calculate total tons and divide by total field size to get t/ha
          const totalFieldSize = validYearDataInTons.reduce((sum, data) => sum + data.fieldSize, 0);

          if (totalFieldSize === 0) {
            return {
              label: year,
              data: {
                min: null,
                avg: null,
                max: null,
              },
            };
          }

          // Calculate total tons across all fields
          const totalMin = validYearDataInTons.reduce((sum, data) => sum + data.min, 0);
          const totalAvg = validYearDataInTons.reduce((sum, data) => sum + data.avg, 0);
          const totalMax = validYearDataInTons.reduce((sum, data) => sum + data.max, 0);

          // Divide by total field size to get t/ha
          const result = {
            min: totalMin / totalFieldSize,
            avg: totalAvg / totalFieldSize,
            max: totalMax / totalFieldSize,
          };

          return {
            label: year,
            data: result,
          };
        }

        // socConcentration and humus are measured in % so we return the weighted average based on field size
        // (we exclude null values from the count, so we need to calculate the appropriate field size for each year )
        const validYearData = yearData.filter((data) => data.min !== null && data.avg !== null && data.max !== null);

        if (validYearData.length === 0) {
          return {
            label: year,
            data: {
              min: null,
              avg: null,
              max: null,
            },
          };
        }

        const totalFieldSize = validYearData.reduce((sum, data) => sum + data.fieldSize, 0);

        if (totalFieldSize === 0) {
          return {
            label: year,
            data: {
              min: null,
              avg: null,
              max: null,
            },
          };
        }

        const weightedMin = validYearData.reduce((sum, data) => {
          const weight = data.fieldSize / totalFieldSize;
          return sum + (data.min! + data.metricCorrectionDelta) * weight;
        }, 0);

        const weightedAvg = validYearData.reduce((sum, data) => {
          const weight = data.fieldSize / totalFieldSize;
          return sum + (data.avg! + data.metricCorrectionDelta) * weight;
        }, 0);

        const weightedMax = validYearData.reduce((sum, data) => {
          const weight = data.fieldSize / totalFieldSize;
          return sum + (data.max! + data.metricCorrectionDelta) * weight;
        }, 0);

        return {
          label: year,
          data: {
            min: weightedMin,
            avg: weightedAvg,
            max: weightedMax,
          },
        };
      })
    );

    return fillYearGaps({
      [EVALUATION_METRICS.HUMUS]: humusDataset,
      [EVALUATION_METRICS.SOC]: socDataset,
      [EVALUATION_METRICS.CO2]: co2Dataset,
    });
  }

  return null;
};

export const normalizeDataWithBaseline = (data: EvaluationMetricDatasetItem[], baseline: number) =>
  data.map((item) => ({
    ...item,
    data: {
      min: item.data.min !== null ? item.data.min - baseline : null,
      avg: item.data.avg !== null ? item.data.avg - baseline : null,
      max: item.data.max !== null ? item.data.max - baseline : null,
    },
  }));

export const getAvailableEvaluationMetrics = (datasets: EvaluationMetricDatasets): EVALUATION_METRICS[] => {
  if (!datasets) return [];

  const metrics: EVALUATION_METRICS[] = [...METRICS_VALUES];

  return metrics.filter((metric) => {
    const metricDatasets = datasets[metric];
    if (!metricDatasets || !Array.isArray(metricDatasets)) return false;

    return metricDatasets.some((dataset) => {
      const { min, avg, max } = dataset.data;
      return min !== null && avg !== null && max !== null;
    });
  });
};

export const getAvailableYearsByEvaluationMetric = (datasets: EvaluationMetricDatasets): EvaluationMetricYearRanges => {
  if (!datasets) return {};

  const metrics: EVALUATION_METRICS[] = [...METRICS_VALUES];
  const yearRanges: EvaluationMetricYearRanges = {};

  metrics.forEach((metric) => {
    const metricDatasets = datasets[metric];
    if (!metricDatasets || !Array.isArray(metricDatasets)) return;

    const validDatasets = metricDatasets.filter((dataset) => {
      const { min, avg, max } = dataset.data;
      return min !== null && avg !== null && max !== null;
    });

    if (validDatasets.length > 0) {
      const sortedDatasets = validDatasets.sort((a, b) => parseInt(a.label) - parseInt(b.label));
      const firstYear = sortedDatasets[0].label;
      const lastYear = sortedDatasets[sortedDatasets.length - 1].label;

      // Find valid years where both current and previous year have non-null values
      const validYears: string[] = [];
      for (let i = 1; i < sortedDatasets.length; i += 1) {
        const currentDataset = sortedDatasets[i];
        const previousDataset = sortedDatasets[i - 1];

        const currentHasValue = currentDataset.data.min !== null && currentDataset.data.avg !== null && currentDataset.data.max !== null;
        const previousHasValue = previousDataset.data.min !== null && previousDataset.data.avg !== null && previousDataset.data.max !== null;

        if (currentHasValue && previousHasValue) {
          validYears.push(currentDataset.label);
        }
      }

      yearRanges[metric] = {
        firstYear,
        lastYear,
        validYears,
      };
    }
  });

  return yearRanges;
};

export const getEvaluationMetricAllYearsLabel = (availableYearsByMetric: EvaluationMetricYearRanges, selectedMetric: EVALUATION_METRICS | "payout") => {
  if (selectedMetric === "payout") return "";

  const firstYear = availableYearsByMetric[selectedMetric]?.firstYear;
  const lastYear = availableYearsByMetric[selectedMetric]?.lastYear;
  if (!firstYear || !lastYear) return "";

  return `${firstYear} - ${lastYear}`;
};

export const getEvaluationMetricsUnitOfMeasure = (metric: EVALUATION_METRICS, calculationType: IMPACT_PANEL_CALCULATION_TYPE) => {
  if ((metric === EVALUATION_METRICS.SOC || metric === EVALUATION_METRICS.CO2) && calculationType === IMPACT_PANEL_CALCULATION_TYPE.PER_HA) {
    return "t/ha";
  }
  return EVALUATION_METRICS_UNIT_OF_MEASURE[metric];
};

export const formatChangeString = (value: number, metric: EVALUATION_METRICS, calculationType: IMPACT_PANEL_CALCULATION_TYPE) => {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${formatNumber({ number: value, decimalPlaces: 2 })} ${getEvaluationMetricsUnitOfMeasure(metric, calculationType)}`;
};

export const calculateMetricChangeFromBaseline = (
  datasets: EvaluationMetricDatasets,
  availableYearsByMetric: EvaluationMetricYearRanges,
  selectedMetric: EVALUATION_METRICS,
  selectedYear: string,
  isAllYearsView: boolean
): { value: number | null; change: "positive" | "negative" | null } => {
  if (!datasets || !datasets[selectedMetric]) return { value: null, change: null };

  const metricDatasets = datasets[selectedMetric];
  const baseline = metricDatasets[0]?.data.avg ?? 0;

  if (isAllYearsView) {
    // For all years view: lastYear avg - baseline
    const lastYear = availableYearsByMetric[selectedMetric]?.lastYear;
    if (!lastYear) return { value: null, change: null };

    const lastYearDataset = metricDatasets.find((dataset) => dataset.label === lastYear);
    const lastYearAvg = lastYearDataset?.data.avg;

    if (lastYearAvg === null || lastYearAvg === undefined) return { value: null, change: null };

    const difference = lastYearAvg - baseline;
    return {
      value: difference,
      change: difference >= 0 ? "positive" : "negative",
    };
  }

  // For specific year view: selectedYear avg - baseline avg
  const selectedYearDataset = metricDatasets.find((dataset) => dataset.label === selectedYear);
  const selectedYearAvg = selectedYearDataset?.data.avg;

  if (selectedYearAvg === null || selectedYearAvg === undefined) return { value: null, change: null };

  const difference = selectedYearAvg - baseline;
  return {
    value: difference,
    change: difference >= 0 ? "positive" : "negative",
  };
};

export const getAvailableYearsByMetricForCorrections = (datasets: EvaluationMetricDatasets): { [key in EVALUATION_METRICS]?: string[] } => {
  if (!datasets) return {};

  const metrics: EVALUATION_METRICS[] = [...METRICS_VALUES];
  const yearRanges: { [key in EVALUATION_METRICS]?: string[] } = {};

  metrics.forEach((metric) => {
    const metricDatasets = datasets[metric];
    if (!metricDatasets || !Array.isArray(metricDatasets)) return;

    if (metricDatasets.length > 0) {
      const sortedDatasets = metricDatasets
        .sort((a, b) => parseInt(a.label) - parseInt(b.label))
        .filter((dataset) => {
          const { min, avg, max } = dataset.data;
          return min !== null && avg !== null && max !== null && parseInt(dataset.label) <= moment().year();
        })
        .map((validDataset) => validDataset.label);

      yearRanges[metric] = sortedDatasets;
    }
  });

  return yearRanges;
};

const getCorrectionDelta = (
  metric: EVALUATION_METRICS,
  agronomicalEvaluationMetrics: AgronomicalEvaluationAggregationsByYear | undefined,
  correction: FieldEvaluationMetricCorrectionItem | undefined
): number => {
  if (!agronomicalEvaluationMetrics || !correction) return 0;

  const correctionYear = correction.year;
  const metricAggregationForCorrectionYear = agronomicalEvaluationMetrics[correctionYear];

  if (metricAggregationForCorrectionYear) {
    const metricData = metricAggregationForCorrectionYear[metric];
    if (!metricData || metricData.avg === null) return 0;

    return correction.value - metricData.avg;
  }

  return 0;
};
